/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import numeral from 'numeral';
import { padding } from 'polished';
import t from 'app/utils/translate';
import Responsive from 'app/components/responsive/Responsive';
import SalaryStatusBlock from './SalaryStatusBlock';
import SalaryUtilisation from './SalaryUtilisation';

type Props = {
  currentStep: number,
  salaryExpenditure: number,
  salaryCap?: number,
  totalSteps?: number,
  noTopRadius?: boolean,
  showUtilisation?: boolean
};

const Box = styled.div(({ theme, noTopRadius }) => ({
  backgroundColor: theme.color.basis2,
  color: theme.color.white,
  paddingTop: theme.spacing.small,
  [theme.mediaQuery.desktopAndUp]: {
    borderTopLeftRadius: noTopRadius ? 0 : theme.borderRadius.default,
    borderTopRightRadius: noTopRadius ? 0 : theme.borderRadius.default,
    paddingBottom: theme.spacing.default,
    paddingTop: theme.spacing.default
  }
}));

const Inner = styled.div({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-between'
});

const LastBlock = styled.div(({ showBorder, theme }) =>
  showBorder
    ? {
        borderLeftColor: theme.color.basis4,
        borderLeftStyle: 'solid',
        borderLeftWidth: 0,
        [theme.mediaQuery.desktopAndUp]: {
          borderLeftWidth: 1
        }
      }
    : {}
);

const SalaryUtilisationWrapper = styled.div(({ theme }) => ({
  paddingTop: theme.spacing.small,
  [theme.mediaQuery.desktopAndUp]: {
    ...padding(theme.spacing.small, theme.spacing.small, '0')
  }
}));

const defaultProps = {
  salaryCap: undefined,
  noTopRadius: false,
  totalSteps: 0,
  showUtilisation: true
};

function SalaryStatus({
  currentStep,
  salaryCap,
  noTopRadius,
  salaryExpenditure,
  showUtilisation,
  totalSteps
}: Props) {
  const selectionsRemaining = totalSteps - currentStep;
  const salaryRemaining = salaryCap - salaryExpenditure;

  return (
    <Box noTopRadius={noTopRadius}>
      <Inner>
        <SalaryStatusBlock title={t('Salary Cap')}>
          {numeral(salaryCap).format('$0,0')}
        </SalaryStatusBlock>
        {showUtilisation && (
          <Responsive desktop>
            <SalaryUtilisation salaryCap={salaryCap} salaryExpenditure={salaryExpenditure} />
          </Responsive>
        )}
        <SalaryStatusBlock warn={salaryRemaining < 0} title={t('Remaining')}>
          {numeral(salaryRemaining).format('$0,0')}
        </SalaryStatusBlock>
        <LastBlock showBorder={showUtilisation}>
          <SalaryStatusBlock title={t('Avg. Remaining')}>
            {selectionsRemaining > 0
              ? numeral(salaryRemaining / selectionsRemaining).format('$0,0')
              : 'N/A'}
          </SalaryStatusBlock>
        </LastBlock>
      </Inner>
      {showUtilisation && (
        <Responsive mobile>
          <SalaryUtilisationWrapper>
            <SalaryUtilisation salaryCap={salaryCap} salaryExpenditure={salaryExpenditure} />
          </SalaryUtilisationWrapper>
        </Responsive>
      )}
    </Box>
  );
}

SalaryStatus.defaultProps = defaultProps;

export default React.memo(SalaryStatus);
